import React, { useEffect, useState, useRef } from "react";
import e3logo from "../assets/e3logo.png";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import authStore from "../stores/AuthStore";
import VerifyEmail from "./VerifyEmail";
import UpdateUserInfo from "./UpdateUserInformation";

import Lottie from "lottie-react";
import loadingAnimation from "../assets/Animation/rocket.json";
import tickMarkAnimation from "../assets/Animation/tick-mark.json";
// import { ConstructionOutlined } from "@mui/icons-material";

const steps = [
  "Validating credentials",
  "Loading user profile",
  "Setting up your dashboard",
];

export default function LoginProgress({
  username,
  password,
  dispatch,
  navigate,
  setLoginSuccess,
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [user, setUser] = useState(null);
  const [loginState, setLoginState] = useState("");
  const [stage, setStage] = useState("loading");
  const hasRun = useRef(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const runLogin = async () => {
      try {
        console.log("🔹 LoginProgress STARTED");
        setCurrentStep(0);
        await delay(500);

        const {
          user: loginUser,
          loginState,
          roles,
        } = await authStore.login(username, password);
        console.log("Login data:", { loginUser, loginState, roles });

        dispatch({
          type: "LOGIN_SUCCESS",
          payload: { authUser: loginUser, authRoles: roles || [] },
        });

        setUser(loginUser);
        setLoginState(loginState);

        const isFirstTimeUser = loginState
          ?.toLowerCase()
          .includes("first time user");
        const isMfaEnabled = loginState
          ?.toLowerCase()
          .includes("mfa is enabled");
        const needsUpdate = loginState
          ?.toLowerCase()
          .includes("update user profile required");

        console.log("→ isFirstTimeUser:", isFirstTimeUser);
        console.log("→ isMfaEnabled:", isMfaEnabled);
        console.log("→ needsUpdate:", needsUpdate);

        if (isFirstTimeUser || isMfaEnabled) {
          setStage("verify");
          return;
        }

        if (needsUpdate) {
          setStage("update");
          return;
        }

        setCurrentStep(1);
        await delay(800);
        setCurrentStep(2);
        await delay(800);

        setLoginSuccess(true);
      } catch (err) {
        const errorMessage =
          err?.response?.data?.message || err?.message || "Login Failed";

        console.log("Here is error message:", errorMessage);
        toast.error(errorMessage);
        setLoginSuccess(false);
      }
    };

    runLogin();
  }, [username, password, dispatch, navigate, setLoginSuccess]);

  const handleVerified = (newLoginState) => {
    setLoginState(newLoginState);
    if (
      newLoginState.toLowerCase().includes("authenticated") ||
      newLoginState.toLowerCase().includes("done verified email")
    ) {
      setLoginSuccess(true);
    } else {
      setStage("update");
    }
  };

  if (stage === "verify") {
    return (
      <VerifyEmail
        user={user}
        loginState={loginState}
        onVerified={handleVerified}
      />
    );
  }

  if (stage === "update") {
    return (
      <UpdateUserInfo
        user={user}
        password={password}
        loginState={loginState}
        setLoginSuccess={setLoginSuccess}
      />
    );
  }

  return (
    <>
      <img src={e3logo} alt="E3 Company Logo" className="w-20 mx-auto mb-4" />
      <h2 className="text-2xl font-bold mb-2">Logging you in...</h2>
      <p
        className="text-lg mb-4"
        style={{ color: isDarkMode ? "#BD93F9" : "#4B5563" }}
      >
        Please wait while we finish logging you in
      </p>
      <div className="text-left space-y-4">
        {steps.map((step, index) => (
          <div key={index} className="flex items-center space-x-4">
            <div className="w-10 h-10 flex items-center justify-center">
              {index < currentStep ? (
                <Lottie
                  animationData={tickMarkAnimation}
                  loop={false}
                  style={{ width: 40, height: 40 }}
                />
              ) : index === currentStep ? (
                <Lottie
                  animationData={loadingAnimation}
                  loop={true}
                  style={{ width: 40, height: 40 }}
                />
              ) : (
                <span className="text-2xl">⬜</span>
              )}
            </div>
            <span className="text-base text-gray-700 dark:text-gray-300">
              {step}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

LoginProgress.propTypes = {
  username: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  setLoginSuccess: PropTypes.func.isRequired,
};
